import { useState } from "react";
import { Icon } from "../common/Icon";

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

interface TeamMember {
  agentName: string;
  agentAvatar?: string;
  role: string;
  responsibility: string;
  reportsTo: string;       // for hub mode
  pipelineOrder: number;   // for pipeline mode
  decisionScope: string;   // for mesh mode
}

interface TaskItem {
  id: string;
  title: string;
  status: "active" | "done" | "paused";
  assignee: string;
  subtasks?: string[];
}

type ArchType = "hub" | "mesh" | "pipeline";

const ROLE_OPTIONS = [
  { value: "coordinator", label: "协调者", icon: "supervisor_account", desc: "统筹全局，分配任务，汇总结果" },
  { value: "researcher", label: "研究员", icon: "biotech", desc: "信息采集，文献调研，深度分析" },
  { value: "analyst", label: "分析师", icon: "analytics", desc: "数据处理，统计验证，建模推理" },
  { value: "editor", label: "编辑", icon: "edit_note", desc: "内容打磨，格式校对，叙事优化" },
  { value: "engineer", label: "工程师", icon: "terminal", desc: "工具开发，流程自动化，技术实现" },
  { value: "scout", label: "情报员", icon: "travel_explore", desc: "外部监控，竞品追踪，趋势捕捉" },
];

const RESPONSIBILITY_PRESETS: Record<string, string[]> = {
  coordinator: ["任务拆解与分配", "进度跟踪与催促", "成果汇总与交付", "冲突协调"],
  researcher: ["论文检索与筛选", "关键信息提取", "方法论对比", "文献综述撰写"],
  analyst: ["数据清洗与预处理", "统计显著性检验", "可视化图表生成", "异常值分析"],
  editor: ["结构化重组", "学术语言润色", "引用格式校验", "逻辑连贯性检查"],
  engineer: ["自动化脚本编写", "数据管线搭建", "API 接口对接", "批量处理工具"],
  scout: ["竞品功能监控", "行业动态追踪", "新论文预警", "会议 deadline 提醒"],
};

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */

export function TeamSettingsPanel({
  participants,
  conversationTitle,
}: {
  participants: { agentName: string; agentAvatar?: string }[];
  conversationTitle: string;
}) {
  const [groupGoal, setGroupGoal] = useState(conversationTitle || "协作完成文献综述");
  const [archType, setArchType] = useState<ArchType>("hub");

  const [members, setMembers] = useState<TeamMember[]>(() =>
    participants.map((p, i) => ({
      agentName: p.agentName,
      agentAvatar: p.agentAvatar,
      role: i === 0 ? "coordinator" : i === 1 ? "researcher" : "analyst",
      responsibility: i === 0 ? "任务拆解与分配、进度跟踪、成果汇总" : i === 1 ? "论文检索与筛选、方法论对比" : "数据清洗、统计检验、可视化",
      reportsTo: i === 0 ? "（自己）" : participants[0]?.agentName || "协调者",
      pipelineOrder: i,
      decisionScope: i === 0 ? "全局决策" : i === 1 ? "研究方向决策" : "数据方法决策",
    })),
  );

  const [tasks, setTasks] = useState<TaskItem[]>([
    { id: "t1", title: "多智能体协调机制分类梳理", status: "active", assignee: participants[0]?.agentName || "Agent", subtasks: ["收集 2024-2026 年核心论文", "按六种范式分类", "绘制分类图谱"] },
    { id: "t2", title: "论文数据统计交叉验证", status: "active", assignee: participants[1]?.agentName || "Agent", subtasks: ["提取各论文实验指标", "统一评估标准", "生成对比表格"] },
    { id: "t3", title: "Related Work 初稿框架设计", status: "paused", assignee: participants[0]?.agentName || "Agent", subtasks: ["确定章节结构", "分配各节负责人"] },
  ]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [expandedMember, setExpandedMember] = useState<number | null>(null);

  /* ─── Handlers ─── */

  const updateMemberRole = (idx: number, roleValue: string) => {
    const presets = RESPONSIBILITY_PRESETS[roleValue] || [];
    setMembers((prev) => prev.map((m, i) =>
      i === idx ? { ...m, role: roleValue, responsibility: presets.slice(0, 3).join("、") } : m
    ));
  };

  const updateMemberField = (idx: number, field: keyof TeamMember, value: string | number) => {
    setMembers((prev) => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const movePipelineOrder = (idx: number, direction: -1 | 1) => {
    setMembers((prev) => {
      const next = [...prev];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= next.length) return prev;
      const currentOrder = next[idx].pipelineOrder;
      next[idx].pipelineOrder = next[targetIdx].pipelineOrder;
      next[targetIdx].pipelineOrder = currentOrder;
      return next.sort((a, b) => a.pipelineOrder - b.pipelineOrder);
    });
  };

  const toggleTaskStatus = (id: string) => {
    setTasks((prev) => prev.map((t) =>
      t.id === id ? { ...t, status: t.status === "done" ? "active" : "done" } : t
    ));
  };

  const addTask = () => {
    if (!newTaskTitle.trim()) return;
    setTasks((prev) => [...prev, {
      id: `t${Date.now()}`, title: newTaskTitle.trim(), status: "active",
      assignee: members[0]?.agentName || "Agent", subtasks: [],
    }]);
    setNewTaskTitle("");
  };

  const removeTask = (id: string) => setTasks((prev) => prev.filter((t) => t.id !== id));

  const updateTaskAssignee = (id: string, assignee: string) => {
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, assignee } : t));
  };

  const roleInfo = (roleValue: string) => ROLE_OPTIONS.find((r) => r.value === roleValue);

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */

  return (
    <div className="px-5 py-5 space-y-7">

      {/* ─── 1. Group Goal ─── */}
      <section>
        <SectionHeader icon="flag" title="群组目标" />
        <input
          type="text" value={groupGoal} onChange={(e) => setGroupGoal(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-sm text-gray-800 dark:text-gray-200 outline-none focus:border-emerald-500/40 transition-colors"
        />
      </section>

      {/* ─── 2. Architecture Selection ─── */}
      <section>
        <SectionHeader icon="account_tree" title="协作架构" />
        <div className="flex gap-2 mb-4">
          {([
            { key: "hub" as const, icon: "hub", label: "中心调度" },
            { key: "mesh" as const, icon: "share", label: "网状协作" },
            { key: "pipeline" as const, icon: "linear_scale", label: "流水线" },
          ]).map((opt) => (
            <button
              key={opt.key}
              onClick={() => setArchType(opt.key)}
              className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded border transition-all ${
                archType === opt.key
                  ? "border-emerald-500/50 bg-emerald-500/[0.06]"
                  : "border-gray-200 dark:border-white/[0.06] hover:border-white/[0.12]"
              }`}
            >
              <Icon name={opt.icon} size={20} className={archType === opt.key ? "text-emerald-400" : "text-gray-500"} />
              <span className={`text-[11px] font-medium ${archType === opt.key ? "text-emerald-300" : "text-gray-500"}`}>{opt.label}</span>
            </button>
          ))}
        </div>

        {/* Architecture-specific description */}
        <div className="p-3 rounded bg-emerald-500/[0.04] border border-emerald-500/10 mb-1">
          <p className="text-[12px] text-gray-400 leading-relaxed">
            {archType === "hub" && "一个 Agent 作为中心协调者，接收任务后拆解分发给团队成员，汇总各方结果并交付。其他 Agent 向协调者汇报。"}
            {archType === "mesh" && "所有 Agent 平等参与，各自拥有决策范围，遇到自己职责内的问题可以直接行动。跨职责的决策需要团队协商。"}
            {archType === "pipeline" && "Agent 按顺序接力执行。每个阶段有明确的输入和输出，上一个 Agent 完成后自动传递给下一个。"}
          </p>
        </div>
      </section>

      {/* ─── 3. Team Roles + Workflow ─── */}
      <section>
        <SectionHeader icon="groups" title={
          archType === "hub" ? "团队角色 & 汇报关系" :
          archType === "pipeline" ? "流水线顺序 & 阶段职责" :
          "团队角色 & 决策范围"
        } />

        <div className="space-y-2">
          {members.map((member, idx) => {
            const ri = roleInfo(member.role);
            const isExpanded = expandedMember === idx;

            return (
              <div key={member.agentName} className="rounded border border-gray-200 dark:border-white/[0.06] overflow-hidden">
                {/* Row header — always visible */}
                <button
                  onClick={() => setExpandedMember(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                >
                  {/* Pipeline order badge */}
                  {archType === "pipeline" && (
                    <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-[11px] font-bold text-emerald-400">{idx + 1}</span>
                    </div>
                  )}

                  {member.agentAvatar ? (
                    <img src={member.agentAvatar} alt="" className="w-9 h-9 rounded bg-white/[0.04] flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold text-sm">{member.agentName.charAt(0)}</span>
                    </div>
                  )}

                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{member.agentName}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-white/[0.06] text-gray-400">
                        {ri?.label || member.role}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{member.responsibility}</p>
                  </div>

                  {/* Pipeline reorder buttons */}
                  {archType === "pipeline" && (
                    <div className="flex flex-col gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => movePipelineOrder(idx, -1)} disabled={idx === 0}
                        className="p-0.5 rounded hover:bg-white/10 disabled:opacity-20">
                        <Icon name="keyboard_arrow_up" size={14} className="text-gray-400" />
                      </button>
                      <button onClick={() => movePipelineOrder(idx, 1)} disabled={idx === members.length - 1}
                        className="p-0.5 rounded hover:bg-white/10 disabled:opacity-20">
                        <Icon name="keyboard_arrow_down" size={14} className="text-gray-400" />
                      </button>
                    </div>
                  )}

                  <Icon name={isExpanded ? "expand_less" : "expand_more"} size={18} className="text-gray-400 flex-shrink-0" />
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-white/[0.04] space-y-3">
                    {/* Role selector */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">角色</label>
                      <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                        {ROLE_OPTIONS.map((r) => (
                          <button
                            key={r.value}
                            onClick={() => updateMemberRole(idx, r.value)}
                            className={`flex flex-col items-center gap-1 py-2 px-1 rounded border text-center transition-all ${
                              member.role === r.value
                                ? "border-emerald-500/50 bg-emerald-500/[0.06]"
                                : "border-gray-200 dark:border-white/[0.06] hover:border-white/[0.12]"
                            }`}
                          >
                            <Icon name={r.icon} size={16} className={member.role === r.value ? "text-emerald-400" : "text-gray-500"} />
                            <span className={`text-[10px] font-medium ${member.role === r.value ? "text-emerald-300" : "text-gray-500"}`}>{r.label}</span>
                          </button>
                        ))}
                      </div>
                      {ri && <p className="text-[11px] text-gray-400 mt-1.5">{ri.desc}</p>}
                    </div>

                    {/* Responsibility editor */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">具体职责</label>
                      <textarea
                        value={member.responsibility}
                        onChange={(e) => updateMemberField(idx, "responsibility", e.target.value)}
                        rows={2}
                        className="w-full mt-1.5 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-[12px] text-gray-700 dark:text-gray-300 outline-none focus:border-emerald-500/30 resize-none"
                      />
                      {/* Quick-fill from presets */}
                      {RESPONSIBILITY_PRESETS[member.role] && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {RESPONSIBILITY_PRESETS[member.role].map((preset) => (
                            <button
                              key={preset}
                              onClick={() => {
                                const current = member.responsibility;
                                if (!current.includes(preset)) {
                                  updateMemberField(idx, "responsibility", current ? `${current}、${preset}` : preset);
                                }
                              }}
                              className="text-[10px] px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-gray-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors"
                            >
                              + {preset}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Architecture-specific field */}
                    {archType === "hub" && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">汇报给</label>
                        <select
                          value={member.reportsTo}
                          onChange={(e) => updateMemberField(idx, "reportsTo", e.target.value)}
                          className="w-full mt-1.5 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-[12px] text-gray-700 dark:text-gray-300 outline-none cursor-pointer"
                        >
                          <option value="（自己）">（自己 — 协调者）</option>
                          {members.filter((_, mi) => mi !== idx).map((m) => (
                            <option key={m.agentName} value={m.agentName}>{m.agentName}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {archType === "mesh" && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">决策范围</label>
                        <input
                          type="text"
                          value={member.decisionScope}
                          onChange={(e) => updateMemberField(idx, "decisionScope", e.target.value)}
                          placeholder="例如：研究方向相关的决策可自主做出"
                          className="w-full mt-1.5 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-[12px] text-gray-700 dark:text-gray-300 placeholder:text-gray-500 outline-none focus:border-emerald-500/30"
                        />
                      </div>
                    )}

                    {archType === "pipeline" && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">阶段输出</label>
                        <input
                          type="text"
                          value={member.responsibility}
                          onChange={(e) => updateMemberField(idx, "responsibility", e.target.value)}
                          placeholder="这个阶段完成后交付什么给下一个人"
                          className="w-full mt-1.5 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-[12px] text-gray-700 dark:text-gray-300 placeholder:text-gray-500 outline-none focus:border-emerald-500/30"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── 4. Task Management ─── */}
      <section>
        <SectionHeader icon="checklist" title="任务管理" />
        <div className="space-y-2">
          {tasks.map((task) => {
            const isExpanded = expandedTask === task.id;
            return (
              <div key={task.id} className="rounded border border-gray-200 dark:border-white/[0.06] overflow-hidden">
                <div className="flex items-center gap-2.5 p-3">
                  <button onClick={() => toggleTaskStatus(task.id)} className="flex-shrink-0">
                    <Icon
                      name={task.status === "done" ? "check_circle" : task.status === "paused" ? "pause_circle" : "radio_button_unchecked"}
                      size={20} filled={task.status === "done"}
                      className={task.status === "done" ? "text-emerald-500" : task.status === "paused" ? "text-amber-500" : "text-gray-400 dark:text-gray-600"}
                    />
                  </button>
                  <button onClick={() => setExpandedTask(isExpanded ? null : task.id)} className="flex-1 min-w-0 text-left">
                    <p className={`text-sm ${task.status === "done" ? "text-gray-400 line-through" : "text-gray-800 dark:text-gray-200"}`}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-400">负责：{task.assignee}</span>
                      {task.subtasks && task.subtasks.length > 0 && (
                        <span className="text-[10px] text-gray-500">· {task.subtasks.length} 个子任务</span>
                      )}
                    </div>
                  </button>
                  <button onClick={() => removeTask(task.id)} className="p-1 rounded-md hover:bg-white/10 flex-shrink-0">
                    <Icon name="close" size={14} className="text-gray-400" />
                  </button>
                </div>

                {/* Expanded: assignee + subtasks */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-white/[0.04] space-y-2.5">
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">负责 Agent</label>
                      <select
                        value={task.assignee}
                        onChange={(e) => updateTaskAssignee(task.id, e.target.value)}
                        className="w-full mt-1 px-3 py-2 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-[12px] text-gray-700 dark:text-gray-300 outline-none cursor-pointer"
                      >
                        {members.map((m) => (
                          <option key={m.agentName} value={m.agentName}>{m.agentName}（{roleInfo(m.role)?.label || m.role}）</option>
                        ))}
                      </select>
                    </div>
                    {task.subtasks && task.subtasks.length > 0 && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">子任务</label>
                        <div className="mt-1 space-y-1">
                          {task.subtasks.map((st, si) => (
                            <div key={si} className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
                              <Icon name="subdirectory_arrow_right" size={14} className="text-gray-400 flex-shrink-0" />
                              <span>{st}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add new task */}
          <div className="flex items-center gap-2">
            <input
              type="text" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTask()}
              placeholder="添加新任务..."
              className="flex-1 px-3.5 py-2.5 rounded bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 outline-none focus:border-emerald-500/40 transition-colors"
            />
            {newTaskTitle.trim() && (
              <button onClick={addTask} className="p-2.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white transition-colors flex-shrink-0">
                <Icon name="add" size={18} />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} size={18} className="text-emerald-500" />
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
    </div>
  );
}
